import { AlertTriangle, Clock, PauseCircle, Search, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/envelope';
import type {
  AutomationTrigger,
  Campaign,
  CampaignAttentionItem,
  CampaignBookingCount,
  CampaignChannel,
  CampaignHomeOverview,
  CampaignMonthBookings,
  CampaignStatusFilter,
} from '../../api/types';
import { RefreshButton } from '../../components/RefreshButton';
import { identityLine } from '../../lib/channelMeta';
import { pct } from '../../lib/commsFormat';
import { formatTenantMoney } from '../../lib/tenantCurrency';
import { useTimeFormat } from '../../lib/tenantTime';
import { type LoadState, REFRESH_FLOOR_MS, useLoad } from '../../lib/useLoad';
import type { AutomationOpen } from './CampaignAutomations';
import { AUDIENCE_LABELS, campaignWhen } from './campaignTools';
import { CampaignsWelcome } from './CampaignsWelcome';
import type { SettingsSection } from './campaignSettingsTools';
import { useComms } from './context';
import { TableRowsSkeleton } from './Skeletons';
import { useInfiniteScroll } from './useInfiniteScroll';
import { useModuleRefresh } from './useModuleRefresh';

const PAGE_SIZE = 25;

const STATUS_TONES: Record<Campaign['status'], { bg: string; fg: string; label: string }> = {
  draft: { bg: 'var(--c-secondary)', fg: 'var(--c-muted)', label: 'Draft' },
  in_review: { bg: 'var(--c-orange-muted)', fg: 'var(--c-orange)', label: 'In review' },
  // Blue like Sending, because that is what it is — a send, with the clock still
  // running down to it. Grey would file it with the drafts nobody has decided on.
  scheduled: { bg: 'var(--c-blue-muted)', fg: 'var(--c-blue)', label: 'Scheduled' },
  sending: { bg: 'var(--c-blue-muted)', fg: 'var(--c-blue)', label: 'Sending' },
  // Amber, not red: a stopped campaign is waiting on a decision, not broken.
  stopped: { bg: 'var(--c-orange-muted)', fg: 'var(--c-orange)', label: 'Stopped' },
  completed: { bg: 'var(--c-green-muted)', fg: 'var(--c-green)', label: 'Sent' },
  failed: { bg: 'var(--c-red-muted)', fg: 'var(--c-red)', label: 'Failed' },
};

/**
 * The filter row, by what an operator is looking for. Drafts include one
 * waiting for review (it is still being written); Stopped and Failed only
 * appear while there is one, so a tenant whose sends all went out is not shown
 * a filter for a state it has never been in.
 */
const SEGMENTS: { key: CampaignStatusFilter | null; label: string; statuses: Campaign['status'][]; always: boolean }[] = [
  { key: null, label: 'All', statuses: [], always: true },
  { key: 'drafts', label: 'Drafts', statuses: ['draft', 'in_review'], always: true },
  { key: 'scheduled', label: 'Scheduled', statuses: ['scheduled'], always: true },
  { key: 'sending', label: 'Sending', statuses: ['sending'], always: true },
  { key: 'stopped', label: 'Stopped', statuses: ['stopped'], always: false },
  { key: 'sent', label: 'Sent', statuses: ['completed'], always: true },
  { key: 'failed', label: 'Failed', statuses: ['failed'], always: false },
];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const monthName = (key: string) => MONTHS[Number(key.slice(5, 7)) - 1] ?? '';

/** A whole-number share for a table cell; the tiles carry the decimal. */
const pct0 = (fraction: number) => `${Math.round(fraction * 100)}%`;

const ratio = (part: number, whole: number) => (whole > 0 ? part / whole : null);

/**
 * "▲ 18%" / "▼ 2.1 pts" against the same span of last month — or nothing when
 * last month has nothing to compare with, because a change from zero is not a
 * percentage and a green "+100%" on a first month would be a lie told in green.
 */
function Delta({ change, unit, versus }: { change: number | null; unit: '%' | 'pts'; versus: string }) {
  if (change === null || !Number.isFinite(change)) return null;
  const shown = unit === '%' ? `${Math.abs(Math.round(change * 100))}%` : `${Math.abs(change).toFixed(1)} pts`;
  const flat = unit === '%' ? Math.round(change * 100) === 0 : Math.abs(change) < 0.05;
  return (
    <span className={`chome-delta${flat ? '' : change > 0 ? ' up' : ' down'}`}>
      {flat ? 'level' : `${change > 0 ? '▲' : '▼'} ${shown}`} <span>vs {versus}</span>
    </span>
  );
}

function MonthTiles({
  overview,
  bookings,
}: {
  overview: CampaignHomeOverview;
  bookings: LoadState<CampaignMonthBookings>;
}) {
  const { current, previous, window } = overview;
  const versus = monthName(window.previousMonth).slice(0, 3);
  const growth = (now: number, then: number) => (then > 0 ? (now - then) / then : null);
  const points = (now: number | null, then: number | null) =>
    now === null || then === null ? null : (now - then) * 100;
  const openNow = ratio(current.opened, current.emailSent);
  const openThen = ratio(previous.opened, previous.emailSent);
  const clickNow = ratio(current.clicked, current.clickTracked);
  const clickThen = ratio(previous.clicked, previous.clickTracked);
  const booked = bookings.data?.current;
  const bookedThen = bookings.data?.previous;
  // A booking count nobody could read is "couldn't check" — never a zero.
  const bookedValue = bookings.error
    ? null
    : booked
      ? booked.checked
        ? booked.bookings.toLocaleString()
        : null
      : '…';
  const bookedReason = bookings.error
    ? 'Could not read reservations from dispatch'
    : booked && !booked.checked
      ? (booked.reason ?? 'Could not read reservations from dispatch')
      : null;
  return (
    <section className="chome-month" aria-label={`${monthName(window.month)} so far`}>
      <div className="chome-section-head">
        <h2>{monthName(window.month)} so far</h2>
        <span>
          Email and text, campaigns and automations — against the same days of {monthName(window.previousMonth)}
        </span>
      </div>
      <div className="chome-tiles">
        <div className="chome-tile">
          <div className="chome-tile-label">Sent</div>
          <div className="chome-tile-value">{current.sent.toLocaleString()}</div>
          <Delta change={growth(current.sent, previous.sent)} unit="%" versus={versus} />
        </div>
        <div className="chome-tile">
          <div className="chome-tile-label">Open rate</div>
          <div className="chome-tile-value">{openNow === null ? '—' : pct(openNow)}</div>
          {/* Over email alone: a text has no open, and averaging texts in would
              read as engagement collapsing the month somebody first texted. */}
          <div className="chome-tile-foot">
            <Delta change={points(openNow, openThen)} unit="pts" versus={versus} />
            <span>{current.emailSent ? 'email only' : 'no email sent yet'}</span>
          </div>
        </div>
        <div className="chome-tile">
          <div className="chome-tile-label">Clicked</div>
          <div className="chome-tile-value">{clickNow === null ? '—' : pct(clickNow)}</div>
          <div className="chome-tile-foot">
            <Delta change={points(clickNow, clickThen)} unit="pts" versus={versus} />
            <span>{current.clickTracked ? 'of sends that tracked clicks' : 'nothing sent tracked clicks'}</span>
          </div>
        </div>
        <div className="chome-tile">
          <div className="chome-tile-label">Replies</div>
          <div className="chome-tile-value">{current.replied.toLocaleString()}</div>
          <div className="chome-tile-foot">
            <Delta change={growth(current.replied, previous.replied)} unit="%" versus={versus} />
            <span>conversations started</span>
          </div>
        </div>
        <div className="chome-tile accent" title={bookedReason ?? undefined}>
          <div className="chome-tile-label">Reservations that followed</div>
          <div className={`chome-tile-value${bookedValue === null ? ' unknown' : ''}`}>
            {bookedValue ?? 'Couldn’t check'}
          </div>
          <div className="chome-tile-foot">
            {booked?.checked && bookedThen?.checked ? (
              <Delta change={growth(booked.bookings, bookedThen.bookings)} unit="%" versus={versus} />
            ) : null}
            <span>{bookedReason ?? `within ${booked?.days ?? 7} days of a send`}</span>
          </div>
        </div>
        {/* Money only for somebody dispatch lets see it (`revenue` null
            otherwise) — the tile is left out rather than shown empty. */}
        {booked?.checked && booked.revenue !== null && (
          <div className="chome-tile accent">
            <div className="chome-tile-label">Reservation value</div>
            <div className="chome-tile-value">{formatTenantMoney(booked.revenue, 0)}</div>
            <div className="chome-tile-foot">
              <span>what followed, not proof</span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

const ATTENTION: Record<
  CampaignAttentionItem['kind'],
  { chip: string; tone: 'orange' | 'red' | 'blue'; icon: typeof AlertTriangle; action: string }
> = {
  automation: { chip: 'Automation', tone: 'orange', icon: AlertTriangle, action: 'Fix it' },
  not_sent: { chip: 'Didn’t send', tone: 'red', icon: XCircle, action: 'Open it' },
  failed: { chip: 'Failed', tone: 'red', icon: XCircle, action: 'See what went out' },
  // Amber like the list's chip and the campaign's own page: waiting on a person.
  in_review: { chip: 'In review', tone: 'orange', icon: Clock, action: 'Review' },
  stopped: { chip: 'Stopped', tone: 'orange', icon: PauseCircle, action: 'Decide' },
};

function NeedsAttention({
  items,
  more,
  onOpenCampaign,
  onOpenAutomation,
}: {
  items: CampaignAttentionItem[];
  more: number;
  onOpenCampaign: (id: number) => Promise<void>;
  onOpenAutomation: (id: number, name: string) => void;
}) {
  const { agents } = useComms();
  const { listTime } = useTimeFormat();
  const [opening, setOpening] = useState<number | null>(null);
  const sentence = (item: CampaignAttentionItem) => {
    switch (item.kind) {
      case 'automation':
        return item.campaignId ? 'failed on its last run.' : 'could not send its last run.';
      case 'not_sent':
        return 'did not go out.';
      case 'failed':
        return 'stopped part way through.';
      case 'in_review':
        return 'is waiting for someone who can send it.';
      default:
        return 'was stopped part way, and is waiting on a decision.';
    }
  };
  const detail = (item: CampaignAttentionItem) => {
    if (item.kind === 'in_review') {
      const author = agents.find((agent) => agent.dashBoardId === item.authorId)?.name;
      return [author ? `Written by ${author}` : null, `waiting since ${listTime(item.at)}`]
        .filter(Boolean)
        .join(' · ');
    }
    if (item.kind === 'stopped' && item.sent !== null && item.sent !== undefined) {
      return `${item.sent.toLocaleString()} sent · ${(item.remaining ?? 0).toLocaleString()} not yet`;
    }
    return item.detail;
  };
  return (
    <section className="chome-attention" aria-label="Needs attention">
      {items.map((item, index) => {
        const meta = ATTENTION[item.kind];
        const Icon = meta.icon;
        const key = `${item.kind}:${item.automationId ?? ''}:${item.campaignId ?? ''}`;
        const busy = opening === index;
        return (
          <div className="chome-attention-row" key={key}>
            <span className={`chome-chip ${meta.tone}`}>
              <Icon size={13} />
              {meta.chip}
            </span>
            <div className="chome-attention-text">
              <b>{item.name}</b> {sentence(item)}{' '}
              {detail(item) && <span className="chome-muted">{detail(item)}</span>}
            </div>
            <button
              type="button"
              className="cbtn"
              disabled={busy}
              onClick={() => {
                if (item.kind === 'automation' && item.automationId !== null) {
                  onOpenAutomation(item.automationId, item.name);
                  return;
                }
                if (item.campaignId === null) return;
                setOpening(index);
                onOpenCampaign(item.campaignId).finally(() => setOpening(null));
              }}
            >
              {busy && <span className="btn-spin" />}
              {meta.action}
            </button>
          </div>
        );
      })}
      {more > 0 && (
        <div className="chome-attention-more">
          and {more} more — {more === 1 ? 'it is' : 'they are'} in the list below
        </div>
      )}
    </section>
  );
}

function BookedCell({ campaign, count }: { campaign: Campaign; count: CampaignBookingCount | 'loading' | undefined }) {
  if (campaign.audienceType === 'drivers') {
    return (
      <span className="chome-muted" title="Chauffeurs do not book">
        —
      </span>
    );
  }
  if (!campaign.stats?.sent) return <span className="chome-muted">—</span>;
  if (count === 'loading' || count === undefined) return <span className="chome-muted">…</span>;
  if (!count.checked) {
    return (
      <span className="chome-unknown" title={`Couldn’t check: ${count.reason ?? 'dispatch could not be read'}`}>
        ?
      </span>
    );
  }
  return (
    <b title={`Trips booked within ${count.days} days of receiving it — what followed, not proof`}>
      {count.bookings.toLocaleString()}
    </b>
  );
}

function CampaignRows({
  campaigns,
  counts,
  onOpen,
}: {
  campaigns: Campaign[];
  counts: Map<number, CampaignBookingCount | 'loading'>;
  onOpen: (campaign: Campaign) => void;
}) {
  const { zone, formats, listTime } = useTimeFormat();
  const { inboxes } = useComms();
  // A running or stopped campaign's share is on its Status chip already, so
  // When says when it began rather than the same percentage twice.
  const whenOf = (campaign: Campaign) =>
    (campaign.status === 'sending' || campaign.status === 'stopped') && campaign.startedAt
      ? `Started ${listTime(campaign.startedAt)}`
      : campaignWhen(campaign, zone, formats);
  const senderOf = (campaign: Campaign) => {
    const inbox = inboxes.find((candidate) => candidate.id === campaign.inboxId);
    // A campaign outlives the channel it went out from, so the row says nothing
    // rather than an id nobody can act on.
    return inbox ? identityLine(inbox) : null;
  };
  return (
    <>
      {campaigns.map((campaign, index) => {
        const stats = campaign.stats;
        const sms = campaign.channel === 'sms';
        const tone = STATUS_TONES[campaign.status];
        const sent = stats?.sent ?? 0;
        const neverStarted = campaign.status === 'failed' && !campaign.startedAt;
        const progress =
          campaign.status === 'sending' && stats && stats.total > 0
            ? Math.min(1, Math.max(0, (stats.total - stats.pending) / stats.total))
            : null;
        const trouble = [
          stats?.bounced ? `${stats.bounced.toLocaleString()} bounced` : null,
          stats?.failed ? `${stats.failed.toLocaleString()} failed` : null,
        ].filter(Boolean);
        const sender = senderOf(campaign);
        const meta = [
          sms ? 'Text' : 'Email',
          `${AUDIENCE_LABELS[campaign.audienceType]}${
            campaign.audienceType === 'custom' && campaign.customRecipients
              ? ` · ${campaign.customRecipients.length}`
              : ''
          }`,
          sender,
        ]
          .filter(Boolean)
          .join(' · ');
        return (
          <button
            key={campaign.id}
            type="button"
            className={`ctable-row clickable chome-row${index % 2 === 1 ? ' odd' : ''}`}
            title={
              campaign.status === 'draft'
                ? 'Open this draft to finish or send it'
                : campaign.status === 'scheduled'
                  ? 'Open it to move the time, edit it, or call it off'
                  : campaign.status === 'in_review'
                    ? 'Read the email waiting for review'
                    : 'Open this campaign'
            }
            onClick={() => onOpen(campaign)}
          >
            <div className="chome-col-name">
              <div className="ctable-cell-main">{campaign.name}</div>
              <div className="ctable-cell-muted">{meta}</div>
            </div>
            <div className="chome-col-status">
              <span className="cchip" style={{ background: tone.bg, color: tone.fg }}>
                {neverStarted ? 'Didn’t send' : tone.label}
                {progress !== null ? ` · ${pct0(progress)}` : ''}
              </span>
              {progress !== null && (
                <div className="chome-progress" aria-hidden>
                  <span style={{ width: `${Math.round(progress * 100)}%` }} />
                </div>
              )}
              {trouble.length > 0 && <div className="chome-trouble">{trouble.join(' · ')}</div>}
            </div>
            <div className="chome-col-when ctable-cell-muted">{whenOf(campaign)}</div>
            <div className="chome-col-num">{sent ? sent.toLocaleString() : <span className="chome-muted">—</span>}</div>
            <div className="chome-col-num">
              {sms ? (
                <span className="chome-muted" title="A text has no open — the campaign’s page has its delivery receipts">
                  —
                </span>
              ) : sent ? (
                pct0((stats?.opened ?? 0) / sent)
              ) : (
                <span className="chome-muted">—</span>
              )}
            </div>
            <div className="chome-col-num">
              {!campaign.trackClicks ? (
                <span className="chome-muted" title="Click tracking was off for this campaign">
                  —
                </span>
              ) : sent ? (
                pct0((stats?.clicked ?? 0) / sent)
              ) : (
                <span className="chome-muted">—</span>
              )}
            </div>
            <div className="chome-col-num">
              {sent ? (stats?.replied ?? 0).toLocaleString() : <span className="chome-muted">—</span>}
            </div>
            <div className="chome-col-num">
              <BookedCell campaign={campaign} count={counts.get(campaign.id)} />
            </div>
          </button>
        );
      })}
    </>
  );
}

/**
 * The Campaigns home: how this month is going, what is waiting on somebody,
 * and every campaign — or, for a tenant that has never sent one, the
 * first-visit screen that gets them to their first.
 *
 * Every figure is the server's (`GET /campaigns/overview`), never a sum over
 * the rows that happen to be loaded: the list pages at 25, and a month total
 * computed from page one is a number that changes when you scroll.
 */
export function CampaignsHome({
  mayDraft,
  onOpen,
  onNew,
  onTryTest,
  onGuide,
  onSettings,
  onOpenAutomation,
  onAutomations,
}: {
  mayDraft: boolean;
  onOpen: (campaign: Campaign) => void;
  onNew: () => void;
  onTryTest: () => void;
  onGuide: () => void;
  onSettings: (section?: SettingsSection, inboxId?: number) => void;
  onOpenAutomation: (open: AutomationOpen) => void;
  onAutomations: () => void;
}) {
  const { source } = useComms();
  const [statusFilter, setStatusFilter] = useState<CampaignStatusFilter | null>(null);
  const [channel, setChannel] = useState<CampaignChannel | null>(null);
  const [typed, setTyped] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(typed.trim()), 300);
    return () => clearTimeout(timer);
  }, [typed]);

  const [overviewNonce, setOverviewNonce] = useState(0);
  const overview = useLoad(() => source.api.getCampaignOverview(), [source, overviewNonce]);
  const monthBookings = useLoad(() => source.api.getCampaignMonthBookings(), [source, overviewNonce]);

  // Paged accumulation — the endpoint pages at 25 and the list has no visible
  // cap, so a further page appends rather than replacing.
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [counts, setCounts] = useState<Map<number, CampaignBookingCount | 'loading'>>(() => new Map());
  const [listLoading, setListLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [listError, setListError] = useState<ApiError | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [appending, setAppending] = useState(false);
  const pageRef = useRef(1);
  const ticket = useRef(0);
  const floorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (floorTimer.current) clearTimeout(floorTimer.current);
    },
    [],
  );

  /**
   * Bookings that followed, for the rows just loaded — a dispatch read of its
   * own, so a slow dispatch costs the Booked column and never the list. Only
   * rows that sent to somebody who could book are asked about.
   */
  const loadCounts = useCallback(
    (rows: Campaign[]) => {
      const ids = rows
        .filter((row) => (row.stats?.sent ?? 0) > 0 && row.audienceType !== 'drivers')
        .map((row) => row.id);
      if (!ids.length) return;
      setCounts((current) => {
        const next = new Map(current);
        for (const id of ids) next.set(id, 'loading');
        return next;
      });
      source.api.getCampaignBookingCounts(ids).then(
        (answers) =>
          setCounts((current) => {
            const next = new Map(current);
            for (const answer of answers) next.set(answer.id, answer);
            return next;
          }),
        () =>
          setCounts((current) => {
            const next = new Map(current);
            for (const id of ids) {
              next.set(id, {
                id,
                checked: false,
                reason: 'Could not read reservations',
                days: 7,
                bookings: 0,
                revenue: null,
              });
            }
            return next;
          }),
      );
    },
    [source],
  );

  const fetchList = useCallback(
    (page: number, append: boolean, refresh = false) => {
      const mine = ++ticket.current;
      const startedAt = Date.now();
      if (floorTimer.current) clearTimeout(floorTimer.current);
      if (append) setAppending(true);
      else setListLoading(true);
      setRefreshing(refresh);
      setListError(null);

      // The rows already on screen are the right ones during a refresh, so the
      // spinner is the only feedback — hold it past the response or a fast
      // reload flips for a single frame and reads as a dead button (see useLoad).
      const settle = () => {
        setListLoading(false);
        if (!refresh) return;
        const held = REFRESH_FLOOR_MS - (Date.now() - startedAt);
        if (held <= 0) {
          setRefreshing(false);
          return;
        }
        floorTimer.current = setTimeout(() => {
          if (ticket.current === mine) setRefreshing(false);
        }, held);
      };

      source.api.listCampaigns(page, { status: statusFilter, channel, q: query }).then(
        (rows) => {
          // Cleared before the ticket guard: a superseded append would otherwise
          // leave the button stuck on "Loading…".
          setAppending(false);
          if (ticket.current !== mine) return;
          pageRef.current = page;
          setCampaigns((current) => {
            if (!append) return rows;
            const seen = new Set(current.map((c) => c.id));
            return [...current, ...rows.filter((c) => !seen.has(c.id))];
          });
          setHasMore(rows.length === PAGE_SIZE);
          loadCounts(rows);
          settle();
        },
        (e: unknown) => {
          setAppending(false);
          if (ticket.current !== mine) return;
          setListError(e instanceof ApiError ? e : new ApiError('server', 0, 'Something went wrong.'));
          settle();
        },
      );
    },
    [source, loadCounts, statusFilter, channel, query],
  );

  useEffect(() => {
    // Fetch on mount and on every change of filter (they are in fetchList's
    // dependencies) — same contract as useLoad.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchList(1, false);
  }, [fetchList]);

  const moreSentinel = useInfiniteScroll({
    hasMore,
    // `appending` and not `listLoading`: a refresh replaces page one and leaves
    // `hasMore` standing, and firing page two into the middle of that would
    // append rows the reload is about to drop.
    busy: appending || listLoading,
    onLoadMore: () => fetchList(pageRef.current + 1, true),
  });

  const refresh = () => {
    setCounts(new Map());
    setOverviewNonce((n) => n + 1);
    fetchList(1, false, true);
  };
  useModuleRefresh(refresh);

  const openById = (id: number) =>
    source.api.getCampaign(id).then(onOpen, () => {
      setListError(new ApiError('server', 0, 'That campaign could not be opened.'));
    });

  const statusCounts = overview.data?.counts ?? {};
  const countOf = (statuses: Campaign['status'][]) =>
    statuses.length
      ? statuses.reduce((sum, status) => sum + (statusCounts[status] ?? 0), 0)
      : Object.values(statusCounts).reduce((sum, n) => sum + (n ?? 0), 0);
  const total = countOf([]);
  const filtered = Boolean(statusFilter || channel || query);
  const segment = SEGMENTS.find((candidate) => candidate.key === statusFilter) ?? SEGMENTS[0];

  // The first visit: nothing has ever been written. Decided on the server's
  // count, never on an empty page — a search that matches nothing is not a
  // tenant that has never sent anything.
  if (overview.data && total === 0 && !filtered && !listLoading && campaigns.length === 0) {
    return (
      <CampaignsWelcome
        mayDraft={mayDraft}
        onNew={onNew}
        onTryTest={onTryTest}
        onGuide={onGuide}
        onSettings={onSettings}
        onStartAutomation={(trigger: AutomationTrigger) => onOpenAutomation({ id: null, preset: trigger })}
      />
    );
  }

  return (
    <div className="chome">
      {overview.data && <MonthTiles overview={overview.data} bookings={monthBookings} />}
      {overview.error && !overview.data && (
        <div className="comms-error-note">This month’s figures could not be loaded. {overview.error.message}</div>
      )}
      {overview.data && overview.data.attention.length > 0 && (
        <NeedsAttention
          items={overview.data.attention}
          more={overview.data.moreAttention}
          onOpenCampaign={openById}
          onOpenAutomation={(id, name) => onOpenAutomation({ id, name })}
        />
      )}

      <section className="chome-list" aria-label="All campaigns">
        <div className="chome-toolbar">
          <label className="chome-search">
            <Search size={14} />
            <input
              aria-label="Search campaigns"
              placeholder="Search campaigns"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </label>
          <div className="chome-segments" role="group" aria-label="Status">
            {SEGMENTS.filter(
              (candidate) =>
                candidate.always || candidate.key === statusFilter || countOf(candidate.statuses) > 0,
            ).map((candidate) => (
              <button
                key={candidate.label}
                type="button"
                className={`chome-seg${candidate.key === statusFilter ? ' on' : ''}`}
                aria-pressed={candidate.key === statusFilter}
                onClick={() => setStatusFilter(candidate.key)}
              >
                {candidate.label}
                {overview.data && <span>{countOf(candidate.statuses)}</span>}
              </button>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <select
            className="chome-select"
            aria-label="Channel"
            value={channel ?? ''}
            onChange={(event) => setChannel((event.target.value || null) as CampaignChannel | null)}
          >
            <option value="">Email and text</option>
            <option value="email">Email</option>
            <option value="sms">Text</option>
          </select>
          <RefreshButton
            className="cbtn"
            size={13}
            title="Refresh campaigns"
            refreshing={refreshing}
            busy={listLoading || appending}
            onClick={refresh}
          />
        </div>
        {listError && <div className="comms-error-note">{listError.message}</div>}
        <div className="chome-scroll">
          <div className="ctable chome-table">
            <div className="ctable-head">
              <div className="chome-col-name">CAMPAIGN</div>
              <div className="chome-col-status">STATUS</div>
              <div className="chome-col-when">WHEN</div>
              <div className="chome-col-num">SENT</div>
              <div className="chome-col-num">OPENED</div>
              <div className="chome-col-num">CLICKED</div>
              <div className="chome-col-num">REPLIED</div>
              <div className="chome-col-num" title="Trips booked within 7 days of receiving it">
                BOOKED
              </div>
            </div>
            {listLoading && campaigns.length === 0 && (
              <TableRowsSkeleton columns={[2.2, 1.2, 1.2, 0.6, 0.6, 0.6, 0.6, 0.6]} />
            )}
            <CampaignRows campaigns={campaigns} counts={counts} onOpen={onOpen} />
            {campaigns.length === 0 && !listLoading && (
              <div className="ctable-empty">
                {filtered ? (
                  <>
                    Nothing matches.{' '}
                    <button
                      type="button"
                      className="an-link"
                      onClick={() => {
                        setStatusFilter(null);
                        setChannel(null);
                        setTyped('');
                        setQuery('');
                      }}
                    >
                      Show every campaign
                    </button>
                  </>
                ) : (
                  <>
                    No campaigns yet — start one with New campaign, or{' '}
                    <button type="button" className="an-link" onClick={onGuide}>
                      read how campaigns work
                    </button>
                    .
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        {hasMore && (
          <>
            {/* Scrolling to the end of the list IS the request for more of it;
                the button stays for the times the observer cannot fire. */}
            <div ref={moreSentinel} aria-hidden />
            <button
              type="button"
              className="cbtn"
              style={{ alignSelf: 'center' }}
              disabled={appending}
              onClick={() => fetchList(pageRef.current + 1, true)}
            >
              {appending && <span className="btn-spin" />}
              {appending ? 'Loading…' : 'Load more campaigns'}
            </button>
          </>
        )}
        <div className="chome-foot">
          <span>
            {campaigns.length.toLocaleString()}
            {!channel && !query && overview.data ? ` of ${countOf(segment.statuses).toLocaleString()}` : ''}
            {hasMore ? ' · more load as you scroll' : ''}
          </span>
          <button type="button" className="an-link" onClick={onAutomations}>
            Automation runs are listed under their automation
          </button>
        </div>
      </section>
    </div>
  );
}
